import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  MobileStepper,
  Typography
} from '@mui/material';
import {
  SmartToy as SmartToyIcon,
  OpenInNew as OpenInNewIcon,
  GroupAdd as GroupAddIcon,
  AccountBalanceWallet as WalletIcon,
  KeyboardArrowLeft,
  KeyboardArrowRight
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useCustomerAuth } from '../../contexts/CustomerAuthContext';
import { useCustomerPortal } from '../../contexts/CustomerPortalContext';

// ONBOARDING de bienvenida del portal cliente: diálogo/stepper en la primera
// visita (flag en localStorage, por usuario). Explica qué es tu empleado IA,
// cómo abrirlo, cómo añadir usuarios y a quién pedir más créditos — al partner
// que gestiona la cuenta si managed_by_reseller ≠ null, si no al proveedor.
// No se enseña en modo impersonación (el partner ya conoce el producto).

const seenKey = (userId: number | undefined) => `customer_onboarding_seen_v1:${userId ?? 'anon'}`;

const CustomerOnboardingDialog: React.FC = () => {
  const { t } = useTranslation();
  const { user, isAuthenticated, readOnly, loading: authLoading } = useCustomerAuth();
  const { managedByReseller, loading: portalLoading } = useCustomerPortal();

  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (authLoading || portalLoading) return;
    if (!isAuthenticated || readOnly) return;
    if (localStorage.getItem(seenKey(user?.id)) === '1') return;
    setOpen(true);
  }, [authLoading, portalLoading, isAuthenticated, readOnly, user]);

  const finish = () => {
    localStorage.setItem(seenKey(user?.id), '1');
    setOpen(false);
  };

  const steps = [
    {
      icon: <SmartToyIcon color="primary" sx={{ fontSize: 56 }} />,
      title: t('customer.onboarding.step1Title', 'Tu empleado IA'),
      text: t('customer.onboarding.step1Text', 'Tu cuenta incluye empleados IA: asistentes especializados que trabajan para tu negocio con sus propios créditos. Los verás en «Mi cuenta», cada uno con su estado y su saldo.')
    },
    {
      icon: <OpenInNewIcon color="primary" sx={{ fontSize: 56 }} />,
      title: t('customer.onboarding.step2Title', 'Cómo abrirlo'),
      text: t('customer.onboarding.step2Text', 'En «Mi cuenta», cuando el empleado esté Activo, pulsa «Abrir»: su aplicación se abre en una pestaña nueva y podrás conversar con él desde ahí.')
    },
    {
      icon: <GroupAddIcon color="primary" sx={{ fontSize: 56 }} />,
      title: t('customer.onboarding.step3Title', 'Añade a tu equipo'),
      text: t('customer.onboarding.step3Text', 'Desde «Mi cuenta» → «Usuarios» puedes añadir personas de tu equipo y asignarlas a un empleado. La contraseña generada se muestra una sola vez: guárdala.')
    },
    {
      icon: <WalletIcon color="primary" sx={{ fontSize: 56 }} />,
      title: t('customer.onboarding.step4Title', '¿Necesitas más créditos?'),
      text: managedByReseller
        ? t('customer.onboarding.step4TextManaged', 'Tu cuenta la gestiona {{name}}. Para ampliar créditos o empleados, escríbeles a {{email}}.', {
            name: managedByReseller.name,
            email: managedByReseller.contact_email || t('customer.onboarding.yourContact', 'tu contacto habitual')
          })
        : t('customer.onboarding.step4Text', 'Cuando se agoten, pide más créditos a tu proveedor desde la sección «Soporte» o amplía tu plan.')
    }
  ];

  const isLast = step === steps.length - 1;
  const current = steps[step];

  return (
    <Dialog open={open} onClose={finish} maxWidth="xs" fullWidth>
      <DialogContent sx={{ textAlign: 'center', pt: 5, pb: 2, minHeight: 280 }}>
        {current.icon}
        <Typography variant="h5" sx={{ fontWeight: 700, mt: 2, mb: 1 }}>
          {current.title}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {current.text}
        </Typography>
      </DialogContent>
      <Box px={2}>
        <MobileStepper
          variant="dots"
          steps={steps.length}
          position="static"
          activeStep={step}
          sx={{ bgcolor: 'transparent', justifyContent: 'center' }}
          nextButton={<span />}
          backButton={<span />}
        />
      </Box>
      <DialogActions sx={{ px: 3, pb: 3, justifyContent: 'space-between' }}>
        <Button onClick={finish} color="inherit" sx={{ textTransform: 'none' }}>
          {t('customer.onboarding.skip', 'Omitir')}
        </Button>
        <Box display="flex" gap={1}>
          {step > 0 && (
            <Button
              onClick={() => setStep(step - 1)}
              startIcon={<KeyboardArrowLeft />}
              sx={{ textTransform: 'none' }}
            >
              {t('customer.onboarding.back', 'Atrás')}
            </Button>
          )}
          <Button
            variant="contained"
            onClick={() => (isLast ? finish() : setStep(step + 1))}
            endIcon={isLast ? undefined : <KeyboardArrowRight />}
            sx={{ textTransform: 'none', boxShadow: 'none' }}
          >
            {isLast
              ? t('customer.onboarding.start', 'Empezar')
              : t('customer.onboarding.next', 'Siguiente')}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default CustomerOnboardingDialog;
